import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Header
      "header.home": "Home",
      "header.skills": "Skills",
      "header.whatIDeliver": "What I Deliver",
      "header.howIWork": "How I Work",
      "header.experience": "Experience",
      "header.projects": "Projects",
      "header.openSource": "Open Source",
      "header.education": "Education",
      "header.achievements": "Achievements",
      "header.stats": "Stats",
      "header.resume": "Resume",
      "header.contact": "Contact Me",

      // Greeting Section
      "greeting.title": "Hi all, I'm Hamza",
      "greeting.subTitle2": "I turn business ideas into products that ship.",
      "greeting.subtitle": "As a Senior iOS Developer, I specialize in building and leading modular mobile products from concept to launch with end-to-end ownership. With over 20 production apps delivered—including products with 1M+ downloads—I combine technical expertise in Swift, SwiftUI, UIKit, and Objective-C with a focus on architecture, performance, and release quality.\n\nFrom crafting intuitive user interfaces to implementing complex backend integrations, I focus on delivering robust, seamless solutions that provide real value. I am currently available for senior roles, freelance, and consulting engagements.",
      "greeting.contactMe": "Contact me",
      "greeting.downloadResume": "Download my resume",
      
      // Skills Section
      "skills.title": "Technology Stack ⚙️",
      "skills.subtitle": "Organized by capability — because tools serve the solution, not the other way around",
      "skills.skill1": "⚡ iOS-native development in Swift, SwiftUI, UIKit, and Objective-C — extended with Dart for cross-platform reach — built for performance and platform fluency",
      "skills.skill2.main": "⚡ Architecture patterns applied by context:",
      "skills.skill2.point1": "✔ MVVM, MVC, MVP, MVI for structured, testable codebases",
      "skills.skill2.point2": "✔ Clean Architecture and VIPER for large-scale, modular systems",
      "skills.skill2.point3": "",
      "skills.skill3": "⚡ AI & ML capabilities via TensorFlow, OpenAI API, and NLP integration — for intelligent, context-aware features",
      "skills.skill4": "⚡ Backend & data layer: Firebase, Core Data, SQL, and REST APIs — reliable, scalable, and offline-ready",
      "skills.skill5": "⚡ DevOps & quality: CI/CD pipelines, Fastlane, Automated Testing, TDD, Crashlytics, and Analytics — for consistent, production-grade delivery",
      "skills.proficiency": "Proficiency 🎯",
      
      // Education Section
      "education.title": "Education 🎓",
      "education.subtitle": "Strong academic foundation supporting continuous professional growth and innovation",
      "education.degree": "Bachelor in Computer Science",
      "education.udemyCertification": "Udemy Certification",
      
      // Experience Section
      "experience.title": "Experiences 💼",
      "experience.subtitle": "Proven track record of delivering results across diverse roles and industries",
      "experience.joblogic.desc": "Joined as a Mid-Level iOS Developer, transitioned into a Senior role",
      "experience.joblogic.heading": "Key Responsibilities & Achievements",
      "experience.joblogic.toolsTitle": "Tools & Skills Gained:",
      "experience.joblogic.point1": "Developed and maintained iOS apps using Swift, Objective-C, MVVM, Clean Swift, and Core Data",
      "experience.joblogic.point2": "Migrated Core Data implementation to SQLite and led the migration team",
      "experience.joblogic.point3": "Stabilized the app by fixing critical bugs and achieved a 100% crash-free experience in production",
      "experience.joblogic.point4": "Refactored 80% of the legacy Joblogic codebase, resolving critical crashes and bugs",
      "experience.joblogic.point5": "Also worked remotely with Joblogic UK and Vietnam team, ensuring consistent delivery and quality",
      "experience.joblogic.point6": "Contributed to the Flutter app using Dart, Clean Architecture, and GetX",
      "experience.joblogic.tool1": "JIRA, Azure DevOps, Git",
      "experience.joblogic.tool2": "SwiftUI (personal projects)",
      "experience.joblogic.tool3": "Cross-platform and clean architecture expertise",
      
      "experience.yumyapps.desc": "Joined as a Mid-level iOS Developer, leading end-to-end app development—from initial build to App Store deployment, maintenance, and feature enhancements",
      "experience.yumyapps.heading": "Key Responsibilities & Achievements:",
      "experience.yumyapps.point1": "Built and maintained iOS applications using Swift and UIKit",
      "experience.yumyapps.point2": "Enhanced app functionality and resolved critical bugs to stabilize the product",
      "experience.yumyapps.point3": "Set the foundation for a new product with clean architecture and reusable components",
      "experience.yumyapps.point4": "Utilized Core Data for efficient local storage and persistence",
      "experience.yumyapps.point5": "Applied MVVM and MVC architecture patterns for scalable code",
      "experience.yumyapps.point6": "Collaborated with cross-functional teams for timely delivery and quality assurance",
      
      "experience.vespert.desc": "Began my iOS development career at Vespert Tech, responsible for building and maintaining iOS applications",
      "experience.vespert.heading": "Key Responsibilities & Achievements:",
      "experience.vespert.point1": "Delivered over 15+ apps, helping startups grow and launch successfully",
      "experience.vespert.point2": "Built and launched iOS apps from scratch using Swift, Objective-C, and UIKit",
      "experience.vespert.point3": "Integrated key features like In-App Purchases, WebSockets, and Google AdMob",
      "experience.vespert.point4": "Improved legacy codebases by adopting MVVM/MVC architecture and refactoring",
      "experience.vespert.point5": "Collaborated in Agile teams with active participation in stand-ups and sprint planning",
      "experience.vespert.point6": "Recognized as a top-performing team member for adding value to the company",
      
      // Projects Section
      "projects.title": "My Projects 🛠️",
      "projects.subtitle": "Startups and companies I've contributed to by building and shaping their technology from the ground up",
      "projects.viewProject": "View Project",
      "projects.openSourceTitle": "Open Source Projects",
      "projects.moreProjects": "More Projects",
      
      // Project Descriptions
      "projects.joblogic.desc": "Field service management software suite for SaaS",
      "projects.jalsah.desc": "Social interaction app for real-time chatting, interactive challenges, and games",
      "projects.familytime.desc": "Parental control app with monitoring and blocking capabilities",
      "projects.alfalah.desc": "Islamic lifestyle app featuring Quran, prayers, duas, Qibla direction, and Islamic calendar",
      "projects.getexpert.desc": "Service marketplace app connecting providers and clients",
      "projects.quokka.desc": "Social matchmaking app for one-minute voice calls",
      "projects.scanner.desc": "Document scanning, editing, and PDF generation utility",
      "projects.skuscanner.desc": "An inventory management application that allows users to manage stock, generate and scan barcodes, maintain records, and create detailed reports for tracking and analysis",
      "projects.community.desc": "Social app enabling users to post help requests within local communities",
      "projects.pisiffik.desc": "Modern e-commerce app with online grocery shopping, in-store pickup, home delivery, and a built-in loyalty rewards system",
      "projects.my360key.desc": "App for real-time discovery of nearby shops with map-based navigation and smart door unlocking using MapKit and smart lock APIs",
      "projects.lifesign.desc": "A secure, communication-focused mobile app with real-time messaging to help users stay connected with friends and family through private, instant conversations",
      "projects.flovso.desc": "A social platform for the fashion industry, enabling users to share opinions, engage in discussions, and get community feedback on trends, brands, and designers—featuring user-generated content, likes, comments",
      
      // Achievements Section
      "achievements.title": "Achievements And Certifications 🏆",
      "achievements.subtitle": "Achievements, certifications, award letters and some cool stuff that I have done",
      
      // Achievement Cards
      "achievements.apple.title": "Apple Certified iOS App Development with Swift",
      "achievements.apple.subtitle": "Completed Apple's official curriculum focused on iOS development using Swift. Gained hands-on experience with app architecture, Swift programming, UI design, and deployment. Participated through the Swift Student Challenge and affiliated Apple learning initiatives.",
      
      "achievements.hackathon.title": "Hackathon Winner at AngelHack",
      "achievements.hackathon.subtitle": "Secured first place at AngelHack for developing a mobile application that provided real-time emergency assistance and geo-tracking. Led the iOS development, implemented scalable architecture using Swift and MVVM, and presented the solution to a panel of industry experts.",
      
      "achievements.speaker.title": "Speaker at Mobile Dev Meetups & Conferences",
      "achievements.speaker.subtitle": "Delivered technical talks on iOS architecture patterns including MVVM, Clean Swift, and modular app design. Shared insights on scalable code practices, real-world debugging strategies, and cross-platform development using Flutter and SwiftUI at various local meetups and online sessions.",
      
      // Blogs Section
      "blogs.title": "Blogs",
      "blogs.subtitle": "With Love for Developing cool stuff, I love to write and teach others what I have learnt.",
      
      // Talks Section
      "talks.title": "TALKS",
      "talks.subtitle": "I LOVE TO SHARE MY LIMITED KNOWLEDGE AND GET A SPEAKER BADGE 😅",
      
      // Podcast Section
      "podcast.title": "Podcast 🎙️",
      "podcast.subtitle": "I LOVE TO TALK ABOUT MYSELF AND TECHNOLOGY",
      
      // Contact Section
      "contact.title": "Contact Me ☎️",
      "contact.subtitle": "Discuss a project or just want to say hi? My Inbox is open for all",
      "contact.reachOut": "Reach Out to me!",
      "contact.openForOpportunities": "Open for opportunities:",
      "contact.hireableYes": "Yes",
      "contact.hireableNo": "No",

      // Talk Card
      "talk.slides": "Slides",
      "talk.event": "Event",
      
      // Resume Section
      "resume.title": "Resume",
      "resume.subtitle": "Feel free to download my resume",
      
      // Skills & Expertise Section
      "skillsExpertise.title": "Skills & Expertise 🎯",
      "skillsExpertise.subtitle": "Core technical strengths and professional capabilities that drive impactful results",
      "skillsExpertise.iosDevelopment": "iOS Development",
      "skillsExpertise.leadership": "Leadership & Methodology",
      "skillsExpertise.philosophy": "Vibe Coding Philosophy",
      "skillsExpertise.crossPlatform": "Cross-Platform & Expanding",
      "skillsExpertise.appPublishing": "App Publishing & Distribution",
      
      // Stats Section
      "stats.title": "Fast Facts 📊",
      "stats.subtitle": "A quick snapshot of my career highlights, showcasing experience, achievements, and impact",
      "stats.yearsExperience": "Years Experience",
      "stats.projectsDelivered": "Projects Delivered",
      "stats.uxImprovement": "UX Improvement",
      "stats.clientsCollaborated": "Clients Collaborated With",
      "stats.aiIntegrations": "AI Integrations",
      "stats.systemUptime": "System Uptime",
      
      // Top Button Section
      "topButton.goToTop": "Go to top",

      // How I Work Section
      "howIWork.title": "How I Work 🔄",
      "howIWork.subtitle": "A clear, repeatable process that de-risks your project and ensures on-time, on-budget delivery.",
      "howIWork.phaseLabel": "PHASE {{number}}",
      "howIWork.phase1.title": "Discovery",
      "howIWork.phase1.desc": "Understand the business, the users, and the problem. Define scope, priorities, and success criteria before writing a single line of code.",
      "howIWork.phase2.title": "Design",
      "howIWork.phase2.desc": "Collaborate on UX flows, user journeys, and system architecture. Make strategic decisions about technology, structure, and scalability.",
      "howIWork.phase3.title": "Build",
      "howIWork.phase3.desc": "Develop with clean architecture, modular code, and automated testing. Regular demos and transparent progress updates throughout.",
      "howIWork.phase4.title": "Release",
      "howIWork.phase4.desc": "QA, performance optimization, store submissions, and production deployment. Your product goes live with confidence and zero loose ends.",
      "howIWork.phase5.title": "Scale",
      "howIWork.phase5.desc": "Monitor, analyze, iterate. Post-launch support, feature evolution, and performance improvements to keep your product growing.",

      // What I Deliver Section
      "whatIDeliver.title": "What I Deliver 📦",
      "whatIDeliver.subtitle": "End-to-end capabilities that take your product from concept to market — and keep it growing.",
      "whatIDeliver.item1.title": "Business Discovery & Requirements",
      "whatIDeliver.item1.desc": "I start by understanding your business goals, user needs, and market context — so we build the right product, not just a product.",
      "whatIDeliver.item2.title": "Solution & System Design",
      "whatIDeliver.item2.desc": "Architecture decisions that scale. I design systems for reliability, performance, and maintainability from day one.",
      "whatIDeliver.item3.title": "iOS App Development",
      "whatIDeliver.item3.desc": "Native iOS development using Swift, SwiftUI, and UIKit. Clean architecture, modular codebases, App Store-ready performance.",
      "whatIDeliver.item4.title": "Backend & API Integrations",
      "whatIDeliver.item4.desc": "Connecting your product to payment gateways, third-party services, cloud infrastructure, and custom APIs — reliably.",
      "whatIDeliver.item5.title": "AI Feature Integration",
      "whatIDeliver.item5.desc": "Adding intelligence to products: on-device ML via Core ML, AI-powered features, and smart automation — where it creates real user value.",
      "whatIDeliver.item6.title": "CI/CD & Automated Delivery",
      "whatIDeliver.item6.desc": "Automated pipelines that build, test, and deploy your product. No manual steps. No surprises. Every release is production-ready.",
      "whatIDeliver.item7.title": "Quality Assurance & Release",
      "whatIDeliver.item7.desc": "Thorough testing, App Store submissions, and release management. Your product launches with confidence.",
      "whatIDeliver.item8.title": "Post-Launch Monitoring & Growth",
      "whatIDeliver.item8.desc": "Crash reporting, performance monitoring, user analytics, and continuous improvement. Your product keeps getting better.",

      // Footer Section
      "footer.madeWith": "Made with ❤️ by Ameer"
    }
  },
  fr: {
    translation: {
      // Header
      "header.home": "Accueil",
      "header.skills": "Compétences",
      "header.whatIDeliver": "Ce que je livre",
      "header.howIWork": "Comment je travaille",
      "header.experience": "Expérience",
      "header.projects": "Projets",
      "header.openSource": "Open Source",
      "header.education": "Formation",
      "header.achievements": "Réalisations",
      "header.stats": "Statistiques",
      "header.resume": "CV",
      "header.contact": "Contactez-moi",

      // Greeting Section
      "greeting.title": "Salut à tous, je suis Hamza",
      "greeting.subTitle2": "Je transforme les idées business en produits qui se lancent.",
      "greeting.subtitle": "En tant que développeur iOS senior, je me spécialise dans la construction et la direction de produits mobiles modulaires du concept au lancement avec une propriété de bout en bout. Avec plus de 20 applications en production livrées—dont des produits avec plus d'1M de téléchargements—je combine une expertise technique en Swift, SwiftUI, UIKit et Objective-C avec un focus sur l'architecture, la performance et la qualité des versions.\n\nDe la création d'interfaces utilisateur intuitives à l'implémentation d'intégrations backend complexes, je me concentre sur la fourniture de solutions robustes et fluides qui apportent une réelle valeur. Je suis actuellement disponible pour des rôles seniors, du freelance et des missions de conseil.",
      "greeting.contactMe": "Contactez-moi",
      "greeting.downloadResume": "Télécharger mon CV",
      
      // Skills Section
      "skills.title": "Stack Technologique ⚙️",
      "skills.subtitle": "Développeur iOS curieux toujours prêt à plonger dans la prochaine grande nouveauté",
      "skills.skill1": "⚡ Concevoir et développer des applications mobiles de haute qualité avec des interfaces fluides, réactives et conviviales",
      "skills.skill2.main": "⚡ Créer des applications qui sont :",
      "skills.skill2.point1": "✔ Basées sur des idées solides et évolutives",
      "skills.skill2.point2": "✔ Visuellement attrayantes et engageantes",
      "skills.skill2.point3": "✔ Fonctionnelles et intuitives—exactement comme les utilisateurs s'y attendent",
      "skills.skill3": "⚡ Implémenter des fonctionnalités puissantes en utilisant des technologies comme Swift, UIKit, SwiftUI et Objective-C",
      "skills.skill4": "⚡ Intégrer des services tiers incluant Firebase, Achats In-App, SDK de connexion sociale, MapKit, CallKit, ARKit, Widgets et APIs REST",
      "skills.skill5": "⚡ Suivre des architectures modernes (MVVM, MVC, MVP, Clean Architecture, VIPER) pour des bases de code évolutives et maintenables",
      "skills.proficiency": "Maîtrise 🎯",
      
      // Education Section
      "education.title": "Éducation 🎓",
      "education.subtitle": "Fondation académique solide soutenant la croissance professionnelle continue et l'innovation",
      "education.degree": "Licence en Informatique",
      "education.udemyCertification": "Certification Udemy",
      
      // Experience Section
      "experience.title": "Expériences 💼",
      "experience.subtitle": "Historique prouvé de livraison de résultats dans divers rôles et industries",
      "experience.joblogic.desc": "Rejoint en tant que développeur iOS de niveau intermédiaire, évolué vers un rôle senior",
      "experience.joblogic.heading": "Responsabilités clés et réalisations",
      "experience.joblogic.toolsTitle": "Outils et compétences acquises :",
      "experience.joblogic.point1": "Développé et maintenu des applications iOS en utilisant Swift, Objective-C, MVVM, Clean Swift et Core Data",
      "experience.joblogic.point2": "Migré l'implémentation Core Data vers SQLite et dirigé l'équipe de migration",
      "experience.joblogic.point3": "Stabilisé l'application en corrigeant des bugs critiques et atteint une expérience 100% sans crash en production",
      "experience.joblogic.point4": "Refactorisé 80% de la base de code legacy de Joblogic, résolvant les crashes et bugs critiques",
      "experience.joblogic.point5": "Également travaillé à distance avec les équipes Joblogic UK et Vietnam, assurant une livraison et qualité constantes",
      "experience.joblogic.point6": "Contribué à l'application Flutter en utilisant Dart, Clean Architecture et GetX",
      "experience.joblogic.tool1": "JIRA, Azure DevOps, Git",
      "experience.joblogic.tool2": "SwiftUI (projets personnels)",
      "experience.joblogic.tool3": "Expertise en applications multiplateformes et architecture propre",
      
      "experience.yumyapps.desc": "Rejoint en tant que développeur iOS de niveau intermédiaire, dirigeant le développement d'applications de bout en bout—de la construction initiale au déploiement sur l'App Store, maintenance et améliorations de fonctionnalités",
      "experience.yumyapps.heading": "Responsabilités clés et réalisations :",
      "experience.yumyapps.point1": "Construit et maintenu des applications iOS en utilisant Swift et UIKit",
      "experience.yumyapps.point2": "Amélioré les fonctionnalités de l'application et résolu des bugs critiques pour stabiliser le produit",
      "experience.yumyapps.point3": "Posé les bases d'un nouveau produit avec une architecture propre et des composants réutilisables",
      "experience.yumyapps.point4": "Utilisé Core Data pour un stockage local et une persistance efficaces",
      "experience.yumyapps.point5": "Appliqué les modèles d'architecture MVVM et MVC pour un code évolutif",
      "experience.yumyapps.point6": "Collaboré avec des équipes transversales pour une livraison en temps voulu et une assurance qualité",
      
      "experience.vespert.desc": "Commencé ma carrière de développement iOS chez Vespert Tech, responsable de la construction et maintenance d'applications iOS",
      "experience.vespert.heading": "Responsabilités clés et réalisations :",
      "experience.vespert.point1": "Livré plus de 15 applications, aidant les startups à croître et à se lancer avec succès",
      "experience.vespert.point2": "Construit et lancé des applications iOS à partir de zéro en utilisant Swift, Objective-C et UIKit",
      "experience.vespert.point3": "Intégré des fonctionnalités clés comme les Achats In-App, WebSockets et Google AdMob",
      "experience.vespert.point4": "Amélioré les bases de code legacy en adoptant l'architecture MVVM/MVC et la refactorisation",
      "experience.vespert.point5": "Collaboré dans des équipes Agile avec participation active aux stand-ups et planification de sprints",
      "experience.vespert.point6": "Reconnu comme membre d'équipe très performant pour avoir apporté de la valeur à l'entreprise",
      
      // Projects Section
      "projects.title": "Mes Projets 🛠️",
      "projects.subtitle": "Startups et entreprises auxquelles j'ai contribué en construisant et façonnant leur technologie depuis le début",
      "projects.viewProject": "Voir le Projet",
      "projects.openSourceTitle": "Projets Open Source",
      "projects.moreProjects": "Plus de Projets",
      
      // Project Descriptions
      "projects.joblogic.desc": "Suite logicielle de gestion de services sur le terrain pour SaaS",
      "projects.jalsah.desc": "Application d'interaction sociale pour chat en temps réel, défis interactifs et jeux",
      "projects.familytime.desc": "Application de contrôle parental avec capacités de surveillance et de blocage",
      "projects.alfalah.desc": "Application de style de vie islamique avec Coran, prières, duas, direction Qibla et calendrier islamique",
      "projects.getexpert.desc": "Application marketplace de services connectant fournisseurs et clients",
      "projects.quokka.desc": "Application de rencontres sociales pour appels vocaux d'une minute",
      "projects.scanner.desc": "Utilitaire de numérisation, édition et génération PDF de documents",
      "projects.skuscanner.desc": "Une application de gestion d'inventaire qui permet aux utilisateurs de gérer le stock, générer et scanner des codes-barres, maintenir des enregistrements et créer des rapports détaillés pour le suivi et l'analyse",
      "projects.community.desc": "Application sociale permettant aux utilisateurs de publier des demandes d'aide dans les communautés locales",
      "projects.pisiffik.desc": "Application e-commerce moderne avec épicerie en ligne, retrait en magasin, livraison à domicile et système de récompenses de fidélité intégré",
      "projects.my360key.desc": "Application pour la découverte en temps réel de magasins à proximité avec navigation basée sur la carte et déverrouillage intelligent de portes utilisant MapKit et APIs de serrures intelligentes",
      "projects.lifesign.desc": "Une application mobile sécurisée axée sur la communication avec messagerie en temps réel pour aider les utilisateurs à rester connectés avec leurs amis et leur famille grâce à des conversations privées et instantanées",
      "projects.flovso.desc": "Une plateforme sociale pour l'industrie de la mode, permettant aux utilisateurs de partager des opinions, participer à des discussions et obtenir des commentaires de la communauté sur les tendances, marques et créateurs—avec contenu généré par les utilisateurs, likes, commentaires",
      
      // Achievements Section
      "achievements.title": "Réalisations Et Certifications 🏆",
      "achievements.subtitle": "Réalisations, certifications, lettres de récompense et quelques trucs cool que j'ai faits",
      
      // Achievement Cards
      "achievements.apple.title": "Certifié Apple pour le Développement d'Applications iOS avec Swift",
      "achievements.apple.subtitle": "Terminé le programme officiel d'Apple axé sur le développement iOS utilisant Swift. Acquis une expérience pratique avec l'architecture d'applications, la programmation Swift, la conception UI et le déploiement. Participé au Swift Student Challenge et aux initiatives d'apprentissage affiliées à Apple.",
      
      "achievements.hackathon.title": "Gagnant du Hackathon chez AngelHack",
      "achievements.hackathon.subtitle": "Remporté la première place chez AngelHack pour le développement d'une application mobile offrant une assistance d'urgence en temps réel et un géo-tracking. Dirigé le développement iOS, implémenté une architecture évolutive utilisant Swift et MVVM, et présenté la solution à un panel d'experts de l'industrie.",
      
      "achievements.speaker.title": "Conférencier aux Meetups et Conférences de Développement Mobile",
      "achievements.speaker.subtitle": "Donné des conférences techniques sur les modèles d'architecture iOS incluant MVVM, Clean Swift et la conception d'applications modulaires. Partagé des insights sur les pratiques de code évolutives, les stratégies de débogage du monde réel et le développement cross-platform utilisant Flutter et SwiftUI lors de divers meetups locaux et sessions en ligne.",
      
      // Blogs Section
      "blogs.title": "Blogs",
      "blogs.subtitle": "Avec l'amour pour développer des trucs cool, j'aime écrire et enseigner aux autres ce que j'ai appris.",
      
      // Talks Section
      "talks.title": "CONFÉRENCES",
      "talks.subtitle": "J'ADORE PARTAGER MES CONNAISSANCES LIMITÉES ET OBTENIR UN BADGE DE CONFÉRENCIER 😅",
      
      // Podcast Section
      "podcast.title": "Podcast 🎙️",
      "podcast.subtitle": "J'ADORE PARLER DE MOI ET DE LA TECHNOLOGIE",
      
      // Contact Section
      "contact.title": "Contactez-moi ☎️",
      "contact.subtitle": "Discuter d'un projet ou juste dire bonjour ? Ma boîte de réception est ouverte à tous",
      "contact.reachOut": "Contactez-moi !",
      "contact.openForOpportunities": "Disponible pour des opportunités :",
      "contact.hireableYes": "Oui",
      "contact.hireableNo": "Non",

      // Talk Card
      "talk.slides": "Diapositives",
      "talk.event": "Événement",
      
      // Resume Section
      "resume.title": "CV",
      "resume.subtitle": "N'hésitez pas à télécharger mon CV",
      
      // Skills & Expertise Section
      "skillsExpertise.title": "Compétences et Expertise 🎯",
      "skillsExpertise.subtitle": "Forces techniques fondamentales et capacités professionnelles qui génèrent des résultats impactants",
      "skillsExpertise.iosDevelopment": "Développement iOS",
      "skillsExpertise.leadership": "Leadership et Méthodologie",
      "skillsExpertise.philosophy": "Philosophie de Codage Vibe",
      "skillsExpertise.crossPlatform": "Cross-Platform et Expansion",
      "skillsExpertise.appPublishing": "Publication et Distribution d'Applications",
      
      // Stats Section
      "stats.title": "Faits Rapides 📊",
      "stats.subtitle": "Un aperçu rapide de mes points forts de carrière, mettant en valeur l'expérience, les réalisations et l'impact",
      "stats.yearsExperience": "Années d'Expérience",
      "stats.projectsDelivered": "Projets Livrés",
      "stats.uxImprovement": "Amélioration UX",
      "stats.clientsCollaborated": "Clients Collaborés",
      "stats.aiIntegrations": "Intégrations IA",
      "stats.systemUptime": "Temps de Fonctionnement Système",

      // How I Work Section
      "howIWork.title": "Comment je travaille 🔄",
      "howIWork.subtitle": "Un processus clair et répétable qui réduit les risques de votre projet et garantit une livraison dans les délais et le budget.",
      "howIWork.phaseLabel": "PHASE {{number}}",
      "howIWork.phase1.title": "Découverte",
      "howIWork.phase1.desc": "Comprendre le business, les utilisateurs et le problème. Définir la portée, les priorités et les critères de succès avant d'écrire une seule ligne de code.",
      "howIWork.phase2.title": "Conception",
      "howIWork.phase2.desc": "Collaborer sur les flux UX, les parcours utilisateurs et l'architecture système. Prendre des décisions stratégiques sur la technologie, la structure et la scalabilité.",
      "howIWork.phase3.title": "Développement",
      "howIWork.phase3.desc": "Développer avec une architecture propre, un code modulaire et des tests automatisés. Démonstrations régulières et mises à jour transparentes tout au long du projet.",
      "howIWork.phase4.title": "Mise en production",
      "howIWork.phase4.desc": "QA, optimisation des performances, soumissions à l'App Store et déploiement en production. Votre produit est lancé avec confiance et sans accroc.",
      "howIWork.phase5.title": "Croissance",
      "howIWork.phase5.desc": "Surveiller, analyser, itérer. Support post-lancement, évolution des fonctionnalités et améliorations des performances pour maintenir la croissance de votre produit.",

      // What I Deliver Section
      "whatIDeliver.title": "Ce que je livre 📦",
      "whatIDeliver.subtitle": "Des capacités de bout en bout qui amènent votre produit du concept au marché — et le font croître.",
      "whatIDeliver.item1.title": "Découverte métier et exigences",
      "whatIDeliver.item1.desc": "Je commence par comprendre vos objectifs business, les besoins utilisateurs et le contexte marché — pour construire le bon produit, pas juste un produit.",
      "whatIDeliver.item2.title": "Conception de solution et système",
      "whatIDeliver.item2.desc": "Des décisions d'architecture qui passent à l'échelle. Je conçois des systèmes fiables, performants et maintenables dès le premier jour.",
      "whatIDeliver.item3.title": "Développement iOS",
      "whatIDeliver.item3.desc": "Développement iOS natif avec Swift, SwiftUI et UIKit. Architecture propre, bases de code modulaires, performance prête pour l'App Store.",
      "whatIDeliver.item4.title": "Intégrations backend et API",
      "whatIDeliver.item4.desc": "Connexion de votre produit aux passerelles de paiement, services tiers, infrastructure cloud et APIs personnalisées — de manière fiable.",
      "whatIDeliver.item5.title": "Intégration de fonctionnalités IA",
      "whatIDeliver.item5.desc": "Ajout d'intelligence aux produits : ML embarqué via Core ML, fonctionnalités IA, automatisation intelligente — là où cela crée une vraie valeur utilisateur.",
      "whatIDeliver.item6.title": "CI/CD et livraison automatisée",
      "whatIDeliver.item6.desc": "Pipelines automatisés qui construisent, testent et déploient votre produit. Aucune étape manuelle. Aucune surprise. Chaque version est prête pour la production.",
      "whatIDeliver.item7.title": "Assurance qualité et mise en production",
      "whatIDeliver.item7.desc": "Tests approfondis, soumissions App Store et gestion des versions. Votre produit se lance en toute confiance.",
      "whatIDeliver.item8.title": "Suivi post-lancement et croissance",
      "whatIDeliver.item8.desc": "Rapports de crash, surveillance des performances, analytics utilisateurs et amélioration continue. Votre produit ne cesse de s'améliorer.",

      // Footer
      "footer.madeWith": "Fait avec ❤️"
    }
  },
  it: {
    translation: {
      // Header
      "header.home": "Home",
      "header.skills": "Competenze",
      "header.whatIDeliver": "Cosa consegno",
      "header.howIWork": "Come lavoro",
      "header.experience": "Esperienza",
      "header.projects": "Progetti",
      "header.openSource": "Open Source",
      "header.education": "Formazione",
      "header.achievements": "Risultati",
      "header.stats": "Statistiche",
      "header.resume": "CV",
      "header.contact": "Contattami",

      // Greeting Section
      "greeting.title": "Ciao a tutti, sono Hamza",
      "greeting.subTitle2": "Trasformo le idee di business in prodotti che vengono lanciati.",
      "greeting.subtitle": "Come sviluppatore iOS Senior, mi specializzo nella creazione e guida di prodotti mobili modulari dal concept al lancio con ownership end-to-end. Con oltre 20 app in produzione consegnate—inclusi prodotti con 1M+ download—combino competenze tecniche in Swift, SwiftUI, UIKit e Objective-C con un focus su architettura, performance e qualità delle release.\n\nDalla creazione di interfacce utente intuitive all'implementazione di integrazioni backend complesse, mi concentro nel fornire soluzioni robuste e fluide che creano valore reale. Sono attualmente disponibile per ruoli senior, freelance e incarichi di consulenza.",
      "greeting.contactMe": "Contattami",
      "greeting.downloadResume": "Scarica il mio CV",

      // Skills Section
      "skills.title": "Stack Tecnologico ⚙️",
      "skills.subtitle": "Organizzato per capacità — perché gli strumenti servono la soluzione, non il contrario",
      "skills.skill1": "⚡ Sviluppo nativo iOS in Swift, SwiftUI, UIKit e Objective-C — esteso con Dart per la portata cross-platform — costruito per performance e fluidità sulla piattaforma",
      "skills.skill2.main": "⚡ Pattern architetturali applicati per contesto:",
      "skills.skill2.point1": "✔ MVVM, MVC, MVP, MVI per codebase strutturate e testabili",
      "skills.skill2.point2": "✔ Clean Architecture e VIPER per sistemi modulari su larga scala",
      "skills.skill2.point3": "",
      "skills.skill3": "⚡ Capacità AI & ML tramite TensorFlow, OpenAI API e integrazione NLP — per funzionalità intelligenti e context-aware",
      "skills.skill4": "⚡ Backend & data layer: Firebase, Core Data, SQL e REST API — affidabili, scalabili e pronti per l'offline",
      "skills.skill5": "⚡ DevOps & qualità: pipeline CI/CD, Fastlane, Automated Testing, TDD, Crashlytics e Analytics — per delivery consistente e di livello production",
      "skills.proficiency": "Competenza 🎯",

      // Education Section
      "education.title": "Istruzione 🎓",
      "education.subtitle": "Solida base accademica a supporto della crescita professionale continua e dell'innovazione",
      "education.degree": "Laurea in Informatica",
      "education.udemyCertification": "Certificazione Udemy",

      // Experience Section
      "experience.title": "Esperienze 💼",
      "experience.subtitle": "Comprovata capacità di consegnare risultati in diversi ruoli e settori",
      "experience.joblogic.desc": "Entrato come sviluppatore iOS Mid-Level, transitato a un ruolo Senior",
      "experience.joblogic.heading": "Responsabilità chiave e risultati",
      "experience.joblogic.toolsTitle": "Strumenti e competenze acquisite:",
      "experience.joblogic.point1": "Sviluppato e mantenuto app iOS usando Swift, Objective-C, MVVM, Clean Swift e Core Data",
      "experience.joblogic.point2": "Migrato l'implementazione Core Data a SQLite e guidato il team di migrazione",
      "experience.joblogic.point3": "Stabilizzato l'app correggendo bug critici e raggiunto un'esperienza 100% senza crash in produzione",
      "experience.joblogic.point4": "Refactoring dell'80% del codebase legacy di Joblogic, risolvendo crash e bug critici",
      "experience.joblogic.point5": "Lavorato da remoto con i team Joblogic UK e Vietnam, garantendo consegna e qualità costanti",
      "experience.joblogic.point6": "Contribuito all'app Flutter usando Dart, Clean Architecture e GetX",
      "experience.joblogic.tool1": "JIRA, Azure DevOps, Git",
      "experience.joblogic.tool2": "SwiftUI (progetti personali)",
      "experience.joblogic.tool3": "Competenza in app cross-platform e clean architecture",
      "experience.yumyapps.desc": "Entrato come sviluppatore iOS Mid-Level, guidando lo sviluppo end-to-end—dalla build iniziale al deployment sull'App Store, manutenzione e miglioramenti delle funzionalità",
      "experience.yumyapps.heading": "Responsabilità chiave e risultati:",
      "experience.yumyapps.point1": "Costruito e mantenuto applicazioni iOS usando Swift e UIKit",
      "experience.yumyapps.point2": "Migliorato le funzionalità dell'app e risolto bug critici per stabilizzare il prodotto",
      "experience.yumyapps.point3": "Posto le basi per un nuovo prodotto con architettura pulita e componenti riutilizzabili",
      "experience.yumyapps.point4": "Utilizzato Core Data per storage locale efficiente e persistenza",
      "experience.yumyapps.point5": "Applicato i pattern MVVM e MVC per codice scalabile",
      "experience.yumyapps.point6": "Collaborato con team cross-funzionali per consegna puntuale e assicurazione della qualità",
      "experience.vespert.desc": "Iniziato la carriera di sviluppo iOS presso Vespert Tech, responsabile della creazione e manutenzione di applicazioni iOS",
      "experience.vespert.heading": "Responsabilità chiave e risultati:",
      "experience.vespert.point1": "Consegnato oltre 15 app, aiutando le startup a crescere e lanciarsi con successo",
      "experience.vespert.point2": "Costruito e lanciato app iOS da zero usando Swift, Objective-C e UIKit",
      "experience.vespert.point3": "Integrato funzionalità chiave come In-App Purchase, WebSocket e Google AdMob",
      "experience.vespert.point4": "Migliorato codebase legacy adottando l'architettura MVVM/MVC e il refactoring",
      "experience.vespert.point5": "Collaborato in team Agile con partecipazione attiva a stand-up e pianificazione degli sprint",
      "experience.vespert.point6": "Riconosciuto come membro del team ad alte prestazioni per aver aggiunto valore all'azienda",

      // Projects Section
      "projects.title": "I miei Progetti 🛠️",
      "projects.subtitle": "Startup e aziende a cui ho contribuito costruendo e plasmando la loro tecnologia dalle fondamenta",
      "projects.viewProject": "Vedi Progetto",
      "projects.openSourceTitle": "Progetti Open Source",
      "projects.moreProjects": "Altri Progetti",
      "projects.joblogic.desc": "Suite software di gestione dei servizi sul campo per SaaS",
      "projects.jalsah.desc": "App di interazione sociale per chat in tempo reale, sfide interattive e giochi",
      "projects.familytime.desc": "App di controllo parentale con capacità di monitoraggio e blocco",
      "projects.alfalah.desc": "App di stile di vita islamico con Corano, preghiere, dua, direzione Qibla e calendario islamico",
      "projects.getexpert.desc": "App marketplace di servizi che connette fornitori e clienti",
      "projects.quokka.desc": "App di matchmaking sociale per chiamate vocali di un minuto",
      "projects.scanner.desc": "Utilità per scansione, modifica e generazione PDF di documenti",
      "projects.skuscanner.desc": "Un'applicazione di gestione dell'inventario che consente agli utenti di gestire le scorte, generare e scansionare codici a barre, mantenere i record e creare report dettagliati per il monitoraggio e l'analisi",
      "projects.community.desc": "App sociale che consente agli utenti di pubblicare richieste di aiuto nelle comunità locali",
      "projects.pisiffik.desc": "App di e-commerce moderna con spesa online, ritiro in negozio, consegna a domicilio e sistema di premi fedeltà integrato",
      "projects.my360key.desc": "App per la scoperta in tempo reale di negozi nelle vicinanze con navigazione basata su mappa e sblocco intelligente delle porte usando MapKit e API per serrature intelligenti",
      "projects.lifesign.desc": "Un'app mobile sicura e focalizzata sulla comunicazione con messaggistica in tempo reale per aiutare gli utenti a restare connessi con amici e familiari attraverso conversazioni private e istantanee",
      "projects.flovso.desc": "Una piattaforma sociale per l'industria della moda, che consente agli utenti di condividere opinioni, partecipare a discussioni e ottenere feedback della community su tendenze, brand e designer—con contenuti generati dagli utenti, like, commenti",

      // Achievements Section
      "achievements.title": "Risultati E Certificazioni 🏆",
      "achievements.subtitle": "Risultati, certificazioni, lettere di riconoscimento e alcune cose interessanti che ho fatto",
      "achievements.apple.title": "Certificato Apple per lo Sviluppo di App iOS con Swift",
      "achievements.apple.subtitle": "Completato il curriculum ufficiale di Apple focalizzato sullo sviluppo iOS usando Swift. Acquisita esperienza pratica con l'architettura delle app, la programmazione Swift, il design UI e il deployment. Partecipato attraverso lo Swift Student Challenge e le iniziative di apprendimento affiliate Apple.",
      "achievements.hackathon.title": "Vincitore Hackathon ad AngelHack",
      "achievements.hackathon.subtitle": "Conquistato il primo posto ad AngelHack per lo sviluppo di un'applicazione mobile che forniva assistenza di emergenza in tempo reale e geo-tracking. Guidato lo sviluppo iOS, implementato un'architettura scalabile usando Swift e MVVM, e presentato la soluzione a un panel di esperti del settore.",
      "achievements.speaker.title": "Relatore a Meetup e Conferenze di Sviluppo Mobile",
      "achievements.speaker.subtitle": "Tenuto presentazioni tecniche sui pattern architetturali iOS inclusi MVVM, Clean Swift e il design modulare delle app. Condiviso insights sulle pratiche di codice scalabili, strategie di debugging nel mondo reale e sviluppo cross-platform usando Flutter e SwiftUI in vari meetup locali e sessioni online.",

      // Blogs Section
      "blogs.title": "Blog",
      "blogs.subtitle": "Con passione per lo sviluppo di cose interessanti, amo scrivere e insegnare agli altri ciò che ho imparato.",

      // Talks Section
      "talks.title": "TALK",
      "talks.subtitle": "AMO CONDIVIDERE LE MIE LIMITATE CONOSCENZE E OTTENERE UN BADGE DA SPEAKER 😅",

      // Podcast Section
      "podcast.title": "Podcast 🎙️",
      "podcast.subtitle": "AMO PARLARE DI ME STESSO E DELLA TECNOLOGIA",

      // Contact Section
      "contact.title": "Contattami ☎️",
      "contact.subtitle": "Discutere di un progetto o vuoi solo salutare? La mia casella di posta è aperta a tutti",
      "contact.reachOut": "Contattami!",
      "contact.openForOpportunities": "Aperto a opportunità:",
      "contact.hireableYes": "Sì",
      "contact.hireableNo": "No",

      // Talk Card
      "talk.slides": "Diapositive",
      "talk.event": "Evento",

      // Resume Section
      "resume.title": "CV",
      "resume.subtitle": "Sentiti libero di scaricare il mio CV",

      // Skills & Expertise Section
      "skillsExpertise.title": "Competenze ed Expertise 🎯",
      "skillsExpertise.subtitle": "Punti di forza tecnici fondamentali e capacità professionali che generano risultati di impatto",
      "skillsExpertise.iosDevelopment": "Sviluppo iOS",
      "skillsExpertise.leadership": "Leadership e Metodologia",
      "skillsExpertise.philosophy": "Filosofia Vibe Coding",
      "skillsExpertise.crossPlatform": "Cross-Platform ed Espansione",
      "skillsExpertise.appPublishing": "Pubblicazione e Distribuzione App",

      // Stats Section
      "stats.title": "Fatti Veloci 📊",
      "stats.subtitle": "Una rapida panoramica dei punti salienti della mia carriera, mostrando esperienza, risultati e impatto",
      "stats.yearsExperience": "Anni di Esperienza",
      "stats.projectsDelivered": "Progetti Consegnati",
      "stats.uxImprovement": "Miglioramento UX",
      "stats.clientsCollaborated": "Clienti con cui ho Collaborato",
      "stats.aiIntegrations": "Integrazioni AI",
      "stats.systemUptime": "Uptime del Sistema",

      // Top Button
      "topButton.goToTop": "Torna su",

      // How I Work Section
      "howIWork.title": "Come Lavoro 🔄",
      "howIWork.subtitle": "Un processo chiaro e ripetibile che riduce i rischi del tuo progetto e garantisce la consegna nei tempi e nel budget.",
      "howIWork.phaseLabel": "FASE {{number}}",
      "howIWork.phase1.title": "Scoperta",
      "howIWork.phase1.desc": "Capire il business, gli utenti e il problema. Definire scope, priorità e criteri di successo prima di scrivere una singola riga di codice.",
      "howIWork.phase2.title": "Design",
      "howIWork.phase2.desc": "Collaborare su flussi UX, percorsi utente e architettura di sistema. Prendere decisioni strategiche su tecnologia, struttura e scalabilità.",
      "howIWork.phase3.title": "Sviluppo",
      "howIWork.phase3.desc": "Sviluppare con clean architecture, codice modulare e testing automatizzato. Demo regolari e aggiornamenti trasparenti sui progressi.",
      "howIWork.phase4.title": "Release",
      "howIWork.phase4.desc": "QA, ottimizzazione delle performance, invio agli store e deployment in produzione. Il tuo prodotto va live con fiducia e senza problemi irrisolti.",
      "howIWork.phase5.title": "Crescita",
      "howIWork.phase5.desc": "Monitorare, analizzare, iterare. Supporto post-lancio, evoluzione delle funzionalità e miglioramenti delle performance per mantenere la crescita del prodotto.",

      // What I Deliver Section
      "whatIDeliver.title": "Cosa Consegno 📦",
      "whatIDeliver.subtitle": "Capacità end-to-end che portano il tuo prodotto dal concept al mercato — e lo fanno crescere.",
      "whatIDeliver.item1.title": "Scoperta del Business e Requisiti",
      "whatIDeliver.item1.desc": "Comincio capendo i tuoi obiettivi di business, le esigenze degli utenti e il contesto di mercato — così costruiamo il prodotto giusto, non solo un prodotto.",
      "whatIDeliver.item2.title": "Design della Soluzione e del Sistema",
      "whatIDeliver.item2.desc": "Decisioni architetturali che scalano. Progetto sistemi per affidabilità, performance e manutenibilità fin dal primo giorno.",
      "whatIDeliver.item3.title": "Sviluppo App iOS",
      "whatIDeliver.item3.desc": "Sviluppo iOS nativo con Swift, SwiftUI e UIKit. Clean architecture, codebase modulari, performance pronte per l'App Store.",
      "whatIDeliver.item4.title": "Integrazioni Backend e API",
      "whatIDeliver.item4.desc": "Connettere il tuo prodotto a gateway di pagamento, servizi di terze parti, infrastruttura cloud e API personalizzate — in modo affidabile.",
      "whatIDeliver.item5.title": "Integrazione Funzionalità AI",
      "whatIDeliver.item5.desc": "Aggiungere intelligenza ai prodotti: ML on-device tramite Core ML, funzionalità AI-powered e automazione smart — dove crea vero valore per l'utente.",
      "whatIDeliver.item6.title": "CI/CD e Delivery Automatizzata",
      "whatIDeliver.item6.desc": "Pipeline automatizzate che buildano, testano e deployano il tuo prodotto. Nessun step manuale. Nessuna sorpresa. Ogni release è pronta per la produzione.",
      "whatIDeliver.item7.title": "Quality Assurance e Release",
      "whatIDeliver.item7.desc": "Testing approfondito, invii sull'App Store e gestione delle release. Il tuo prodotto si lancia con fiducia.",
      "whatIDeliver.item8.title": "Monitoraggio Post-Lancio e Crescita",
      "whatIDeliver.item8.desc": "Crash reporting, monitoraggio delle performance, analytics utenti e miglioramento continuo. Il tuo prodotto continua a migliorare.",

      // Footer
      "footer.madeWith": "Fatto con ❤️"
    }
  },
  es: {
    translation: {
      // Header
      "header.home": "Inicio",
      "header.skills": "Habilidades",
      "header.whatIDeliver": "Qué entrego",
      "header.howIWork": "Cómo trabajo",
      "header.experience": "Experiencia",
      "header.projects": "Proyectos",
      "header.openSource": "Open Source",
      "header.education": "Educación",
      "header.achievements": "Logros",
      "header.stats": "Estadísticas",
      "header.resume": "CV",
      "header.contact": "Contáctame",

      // Greeting Section
      "greeting.title": "Hola a todos, soy Hamza",
      "greeting.subTitle2": "Convierto ideas de negocio en productos que se lanzan.",
      "greeting.subtitle": "Como Desarrollador iOS Senior, me especializo en construir y liderar productos móviles modulares desde el concepto hasta el lanzamiento con propiedad end-to-end. Con más de 20 aplicaciones en producción entregadas—incluyendo productos con más de 1M de descargas—combino experiencia técnica en Swift, SwiftUI, UIKit y Objective-C con un enfoque en arquitectura, rendimiento y calidad de lanzamiento.\n\nDesde la creación de interfaces de usuario intuitivas hasta la implementación de integraciones backend complejas, me concentro en entregar soluciones robustas y fluidas que proporcionan valor real. Actualmente estoy disponible para roles senior, freelance y compromisos de consultoría.",
      "greeting.contactMe": "Contáctame",
      "greeting.downloadResume": "Descargar mi CV",

      // Skills Section
      "skills.title": "Stack Tecnológico ⚙️",
      "skills.subtitle": "Organizado por capacidad — porque las herramientas sirven a la solución, no al revés",
      "skills.skill1": "⚡ Desarrollo nativo iOS en Swift, SwiftUI, UIKit y Objective-C — ampliado con Dart para alcance multiplataforma — construido para rendimiento y fluidez en la plataforma",
      "skills.skill2.main": "⚡ Patrones de arquitectura aplicados por contexto:",
      "skills.skill2.point1": "✔ MVVM, MVC, MVP, MVI para bases de código estructuradas y testeables",
      "skills.skill2.point2": "✔ Clean Architecture y VIPER para sistemas modulares a gran escala",
      "skills.skill2.point3": "",
      "skills.skill3": "⚡ Capacidades de IA y ML a través de TensorFlow, OpenAI API e integración NLP — para funciones inteligentes y conscientes del contexto",
      "skills.skill4": "⚡ Capa backend y datos: Firebase, Core Data, SQL y REST APIs — confiables, escalables y listas para uso offline",
      "skills.skill5": "⚡ DevOps y calidad: pipelines CI/CD, Fastlane, Automated Testing, TDD, Crashlytics y Analytics — para entrega consistente de nivel producción",
      "skills.proficiency": "Dominio 🎯",

      // Education Section
      "education.title": "Educación 🎓",
      "education.subtitle": "Sólida base académica que apoya el crecimiento profesional continuo y la innovación",
      "education.degree": "Licenciatura en Informática",
      "education.udemyCertification": "Certificación Udemy",

      // Experience Section
      "experience.title": "Experiencias 💼",
      "experience.subtitle": "Historial comprobado de entrega de resultados en diversos roles e industrias",
      "experience.joblogic.desc": "Incorporado como desarrollador iOS de nivel medio, transitando a un rol Senior",
      "experience.joblogic.heading": "Responsabilidades clave y logros",
      "experience.joblogic.toolsTitle": "Herramientas y habilidades adquiridas:",
      "experience.joblogic.point1": "Desarrollé y mantuve apps iOS usando Swift, Objective-C, MVVM, Clean Swift y Core Data",
      "experience.joblogic.point2": "Migré la implementación de Core Data a SQLite y lideré el equipo de migración",
      "experience.joblogic.point3": "Estabilicé la app corrigiendo bugs críticos y logré una experiencia 100% libre de crashes en producción",
      "experience.joblogic.point4": "Refactoricé el 80% del codebase legacy de Joblogic, resolviendo crashes y bugs críticos",
      "experience.joblogic.point5": "También trabajé de forma remota con los equipos de Joblogic UK y Vietnam, garantizando entrega y calidad consistentes",
      "experience.joblogic.point6": "Contribuí a la app Flutter usando Dart, Clean Architecture y GetX",
      "experience.joblogic.tool1": "JIRA, Azure DevOps, Git",
      "experience.joblogic.tool2": "SwiftUI (proyectos personales)",
      "experience.joblogic.tool3": "Experiencia en apps multiplataforma y clean architecture",
      "experience.yumyapps.desc": "Incorporado como desarrollador iOS de nivel medio, liderando el desarrollo end-to-end—desde la construcción inicial hasta el despliegue en la App Store, mantenimiento y mejoras de funcionalidades",
      "experience.yumyapps.heading": "Responsabilidades clave y logros:",
      "experience.yumyapps.point1": "Construí y mantuve aplicaciones iOS usando Swift y UIKit",
      "experience.yumyapps.point2": "Mejoré la funcionalidad de la app y resolví bugs críticos para estabilizar el producto",
      "experience.yumyapps.point3": "Establecí la base para un nuevo producto con arquitectura limpia y componentes reutilizables",
      "experience.yumyapps.point4": "Utilicé Core Data para almacenamiento local eficiente y persistencia",
      "experience.yumyapps.point5": "Apliqué los patrones de arquitectura MVVM y MVC para código escalable",
      "experience.yumyapps.point6": "Colaboré con equipos interfuncionales para entrega puntual y aseguramiento de calidad",
      "experience.vespert.desc": "Comencé mi carrera de desarrollo iOS en Vespert Tech, responsable de construir y mantener aplicaciones iOS",
      "experience.vespert.heading": "Responsabilidades clave y logros:",
      "experience.vespert.point1": "Entregué más de 15 apps, ayudando a startups a crecer y lanzarse con éxito",
      "experience.vespert.point2": "Construí y lancé apps iOS desde cero usando Swift, Objective-C y UIKit",
      "experience.vespert.point3": "Integré funcionalidades clave como In-App Purchases, WebSockets y Google AdMob",
      "experience.vespert.point4": "Mejoré codebases legacy adoptando arquitectura MVVM/MVC y refactorización",
      "experience.vespert.point5": "Colaboré en equipos Agile con participación activa en stand-ups y planificación de sprints",
      "experience.vespert.point6": "Reconocido como miembro destacado del equipo por agregar valor a la empresa",

      // Projects Section
      "projects.title": "Mis Proyectos 🛠️",
      "projects.subtitle": "Startups y empresas a las que he contribuido construyendo y dando forma a su tecnología desde cero",
      "projects.viewProject": "Ver Proyecto",
      "projects.openSourceTitle": "Proyectos de Código Abierto",
      "projects.moreProjects": "Más Proyectos",
      "projects.joblogic.desc": "Suite de software de gestión de servicios de campo para SaaS",
      "projects.jalsah.desc": "App de interacción social para chat en tiempo real, desafíos interactivos y juegos",
      "projects.familytime.desc": "App de control parental con capacidades de monitoreo y bloqueo",
      "projects.alfalah.desc": "App de estilo de vida islámico con Corán, oraciones, duas, dirección Qibla y calendario islámico",
      "projects.getexpert.desc": "App marketplace de servicios que conecta proveedores y clientes",
      "projects.quokka.desc": "App de matchmaking social para llamadas de voz de un minuto",
      "projects.scanner.desc": "Utilidad de escaneo, edición y generación de PDF de documentos",
      "projects.skuscanner.desc": "Una aplicación de gestión de inventario que permite a los usuarios gestionar el stock, generar y escanear códigos de barras, mantener registros y crear informes detallados para seguimiento y análisis",
      "projects.community.desc": "App social que permite a los usuarios publicar solicitudes de ayuda en comunidades locales",
      "projects.pisiffik.desc": "App de comercio electrónico moderna con compras de comestibles en línea, recogida en tienda, entrega a domicilio y sistema de recompensas de fidelidad integrado",
      "projects.my360key.desc": "App para el descubrimiento en tiempo real de tiendas cercanas con navegación basada en mapas y desbloqueo inteligente de puertas usando MapKit y APIs de cerraduras inteligentes",
      "projects.lifesign.desc": "Una app móvil segura y centrada en la comunicación con mensajería en tiempo real para ayudar a los usuarios a mantenerse conectados con amigos y familia a través de conversaciones privadas e instantáneas",
      "projects.flovso.desc": "Una plataforma social para la industria de la moda, que permite a los usuarios compartir opiniones, participar en discusiones y obtener feedback de la comunidad sobre tendencias, marcas y diseñadores—con contenido generado por usuarios, likes, comentarios",

      // Achievements Section
      "achievements.title": "Logros Y Certificaciones 🏆",
      "achievements.subtitle": "Logros, certificaciones, cartas de reconocimiento y algunas cosas geniales que he hecho",
      "achievements.apple.title": "Certificado Apple para el Desarrollo de Apps iOS con Swift",
      "achievements.apple.subtitle": "Completé el currículo oficial de Apple enfocado en el desarrollo iOS usando Swift. Adquirí experiencia práctica con la arquitectura de apps, programación Swift, diseño de UI y despliegue. Participé a través del Swift Student Challenge e iniciativas de aprendizaje afiliadas a Apple.",
      "achievements.hackathon.title": "Ganador de Hackathon en AngelHack",
      "achievements.hackathon.subtitle": "Obtuve el primer lugar en AngelHack por desarrollar una aplicación móvil que proporcionaba asistencia de emergencia en tiempo real y geo-tracking. Lideré el desarrollo iOS, implementé arquitectura escalable usando Swift y MVVM, y presenté la solución a un panel de expertos de la industria.",
      "achievements.speaker.title": "Ponente en Meetups y Conferencias de Desarrollo Móvil",
      "achievements.speaker.subtitle": "Impartí charlas técnicas sobre patrones de arquitectura iOS incluyendo MVVM, Clean Swift y diseño modular de apps. Compartí insights sobre prácticas de código escalable, estrategias de depuración del mundo real y desarrollo multiplataforma usando Flutter y SwiftUI en varios meetups locales y sesiones en línea.",

      // Blogs Section
      "blogs.title": "Blogs",
      "blogs.subtitle": "Con amor por desarrollar cosas geniales, me encanta escribir y enseñar a otros lo que he aprendido.",

      // Talks Section
      "talks.title": "CHARLAS",
      "talks.subtitle": "ME ENCANTA COMPARTIR MI LIMITADO CONOCIMIENTO Y CONSEGUIR UNA INSIGNIA DE PONENTE 😅",

      // Podcast Section
      "podcast.title": "Podcast 🎙️",
      "podcast.subtitle": "ME ENCANTA HABLAR DE MÍ MISMO Y DE LA TECNOLOGÍA",

      // Contact Section
      "contact.title": "Contáctame ☎️",
      "contact.subtitle": "¿Discutir un proyecto o simplemente quieres saludar? Mi bandeja de entrada está abierta para todos",
      "contact.reachOut": "¡Contáctame!",
      "contact.openForOpportunities": "Abierto a oportunidades:",
      "contact.hireableYes": "Sí",
      "contact.hireableNo": "No",

      // Talk Card
      "talk.slides": "Diapositivas",
      "talk.event": "Evento",

      // Resume Section
      "resume.title": "CV",
      "resume.subtitle": "Siéntete libre de descargar mi CV",

      // Skills & Expertise Section
      "skillsExpertise.title": "Habilidades y Experiencia 🎯",
      "skillsExpertise.subtitle": "Fortalezas técnicas fundamentales y capacidades profesionales que impulsan resultados impactantes",
      "skillsExpertise.iosDevelopment": "Desarrollo iOS",
      "skillsExpertise.leadership": "Liderazgo y Metodología",
      "skillsExpertise.philosophy": "Filosofía Vibe Coding",
      "skillsExpertise.crossPlatform": "Multiplataforma y Expansión",
      "skillsExpertise.appPublishing": "Publicación y Distribución de Apps",

      // Stats Section
      "stats.title": "Datos Rápidos 📊",
      "stats.subtitle": "Una instantánea rápida de los puntos destacados de mi carrera, mostrando experiencia, logros e impacto",
      "stats.yearsExperience": "Años de Experiencia",
      "stats.projectsDelivered": "Proyectos Entregados",
      "stats.uxImprovement": "Mejora de UX",
      "stats.clientsCollaborated": "Clientes con los que he Colaborado",
      "stats.aiIntegrations": "Integraciones de IA",
      "stats.systemUptime": "Tiempo de Actividad del Sistema",

      // Top Button
      "topButton.goToTop": "Ir arriba",

      // How I Work Section
      "howIWork.title": "Cómo Trabajo 🔄",
      "howIWork.subtitle": "Un proceso claro y repetible que reduce el riesgo de tu proyecto y garantiza entrega a tiempo y dentro del presupuesto.",
      "howIWork.phaseLabel": "FASE {{number}}",
      "howIWork.phase1.title": "Descubrimiento",
      "howIWork.phase1.desc": "Entender el negocio, los usuarios y el problema. Definir alcance, prioridades y criterios de éxito antes de escribir una sola línea de código.",
      "howIWork.phase2.title": "Diseño",
      "howIWork.phase2.desc": "Colaborar en flujos UX, recorridos de usuario y arquitectura del sistema. Tomar decisiones estratégicas sobre tecnología, estructura y escalabilidad.",
      "howIWork.phase3.title": "Construcción",
      "howIWork.phase3.desc": "Desarrollar con clean architecture, código modular y testing automatizado. Demos regulares y actualizaciones de progreso transparentes.",
      "howIWork.phase4.title": "Lanzamiento",
      "howIWork.phase4.desc": "QA, optimización de rendimiento, envíos a las tiendas y despliegue en producción. Tu producto sale en vivo con confianza y sin cabos sueltos.",
      "howIWork.phase5.title": "Escalar",
      "howIWork.phase5.desc": "Monitorear, analizar, iterar. Soporte post-lanzamiento, evolución de funcionalidades y mejoras de rendimiento para mantener tu producto creciendo.",

      // What I Deliver Section
      "whatIDeliver.title": "Lo Que Entrego 📦",
      "whatIDeliver.subtitle": "Capacidades end-to-end que llevan tu producto del concepto al mercado — y lo mantienen creciendo.",
      "whatIDeliver.item1.title": "Descubrimiento del Negocio y Requisitos",
      "whatIDeliver.item1.desc": "Comienzo entendiendo tus objetivos de negocio, necesidades de usuario y contexto de mercado — para construir el producto correcto, no solo un producto.",
      "whatIDeliver.item2.title": "Diseño de Solución y Sistema",
      "whatIDeliver.item2.desc": "Decisiones de arquitectura que escalan. Diseño sistemas para confiabilidad, rendimiento y mantenibilidad desde el primer día.",
      "whatIDeliver.item3.title": "Desarrollo de App iOS",
      "whatIDeliver.item3.desc": "Desarrollo iOS nativo usando Swift, SwiftUI y UIKit. Clean architecture, codebases modulares, rendimiento listo para App Store.",
      "whatIDeliver.item4.title": "Integraciones Backend y API",
      "whatIDeliver.item4.desc": "Conectar tu producto a pasarelas de pago, servicios de terceros, infraestructura en la nube y APIs personalizadas — de forma confiable.",
      "whatIDeliver.item5.title": "Integración de Funciones de IA",
      "whatIDeliver.item5.desc": "Añadiendo inteligencia a los productos: ML en dispositivo via Core ML, funciones potenciadas por IA y automatización inteligente — donde crea valor real para el usuario.",
      "whatIDeliver.item6.title": "CI/CD y Entrega Automatizada",
      "whatIDeliver.item6.desc": "Pipelines automatizados que construyen, prueban y despliegan tu producto. Sin pasos manuales. Sin sorpresas. Cada versión está lista para producción.",
      "whatIDeliver.item7.title": "Aseguramiento de Calidad y Lanzamiento",
      "whatIDeliver.item7.desc": "Pruebas exhaustivas, envíos a la App Store y gestión de versiones. Tu producto se lanza con confianza.",
      "whatIDeliver.item8.title": "Monitoreo Post-Lanzamiento y Crecimiento",
      "whatIDeliver.item8.desc": "Reportes de crashes, monitoreo de rendimiento, análisis de usuarios y mejora continua. Tu producto sigue mejorando.",

      // Footer
      "footer.madeWith": "Hecho con ❤️"
    }
  },
  de: {
    translation: {
      // Header
      "header.home": "Start",
      "header.skills": "Fähigkeiten",
      "header.whatIDeliver": "Was ich liefere",
      "header.howIWork": "Wie ich arbeite",
      "header.experience": "Erfahrung",
      "header.projects": "Projekte",
      "header.openSource": "Open Source",
      "header.education": "Ausbildung",
      "header.achievements": "Leistungen",
      "header.stats": "Statistiken",
      "header.resume": "Lebenslauf",
      "header.contact": "Kontakt",

      // Greeting Section
      "greeting.title": "Hallo zusammen, ich bin Hamza",
      "greeting.subTitle2": "Ich verwandle Geschäftsideen in Produkte, die auf den Markt kommen.",
      "greeting.subtitle": "Als Senior iOS-Entwickler spezialisiere ich mich auf den Aufbau und die Leitung modularer mobiler Produkte vom Konzept bis zum Launch mit End-to-End-Verantwortung. Mit über 20 gelieferten Produktions-Apps—einschließlich Produkten mit 1M+ Downloads—kombiniere ich technisches Know-how in Swift, SwiftUI, UIKit und Objective-C mit einem Fokus auf Architektur, Performance und Release-Qualität.\n\nVon der Gestaltung intuitiver Benutzeroberflächen bis zur Implementierung komplexer Backend-Integrationen konzentriere ich mich auf die Bereitstellung robuster, nahtloser Lösungen, die echten Mehrwert bieten. Ich bin derzeit für Senior-Positionen, Freelance- und Beratungsaufträge verfügbar.",
      "greeting.contactMe": "Kontaktiere mich",
      "greeting.downloadResume": "Lebenslauf herunterladen",

      // Skills Section
      "skills.title": "Technologie-Stack ⚙️",
      "skills.subtitle": "Nach Fähigkeiten organisiert — denn Werkzeuge dienen der Lösung, nicht umgekehrt",
      "skills.skill1": "⚡ Natives iOS-Entwicklung in Swift, SwiftUI, UIKit und Objective-C — erweitert mit Dart für plattformübergreifende Reichweite — gebaut für Performance und Plattformflüssigkeit",
      "skills.skill2.main": "⚡ Architekturmuster je nach Kontext angewendet:",
      "skills.skill2.point1": "✔ MVVM, MVC, MVP, MVI für strukturierte, testbare Codebases",
      "skills.skill2.point2": "✔ Clean Architecture und VIPER für große, modulare Systeme",
      "skills.skill2.point3": "",
      "skills.skill3": "⚡ KI & ML-Fähigkeiten über TensorFlow, OpenAI API und NLP-Integration — für intelligente, kontextbewusste Funktionen",
      "skills.skill4": "⚡ Backend & Datenschicht: Firebase, Core Data, SQL und REST APIs — zuverlässig, skalierbar und offline-fähig",
      "skills.skill5": "⚡ DevOps & Qualität: CI/CD-Pipelines, Fastlane, Automated Testing, TDD, Crashlytics und Analytics — für konsistente Lieferung auf Produktionsniveau",
      "skills.proficiency": "Kompetenz 🎯",

      // Education Section
      "education.title": "Ausbildung 🎓",
      "education.subtitle": "Starke akademische Grundlage zur Unterstützung von kontinuierlichem beruflichen Wachstum und Innovation",
      "education.degree": "Bachelor in Informatik",
      "education.udemyCertification": "Udemy-Zertifizierung",

      // Experience Section
      "experience.title": "Erfahrungen 💼",
      "experience.subtitle": "Nachgewiesene Erfolgsbilanz bei der Erzielung von Ergebnissen in verschiedenen Rollen und Branchen",
      "experience.joblogic.desc": "Als Mid-Level iOS-Entwickler eingestiegen, in eine Senior-Rolle aufgestiegen",
      "experience.joblogic.heading": "Hauptverantwortlichkeiten und Leistungen",
      "experience.joblogic.toolsTitle": "Verwendete Tools und erworbene Fähigkeiten:",
      "experience.joblogic.point1": "iOS-Apps mit Swift, Objective-C, MVVM, Clean Swift und Core Data entwickelt und gepflegt",
      "experience.joblogic.point2": "Core Data-Implementierung zu SQLite migriert und das Migrationsteam geleitet",
      "experience.joblogic.point3": "Die App durch Behebung kritischer Bugs stabilisiert und ein 100% absturzfreies Erlebnis in der Produktion erreicht",
      "experience.joblogic.point4": "80% der Legacy-Codebasis von Joblogic refactored und kritische Abstürze und Bugs behoben",
      "experience.joblogic.point5": "Remote mit den Teams Joblogic UK und Vietnam zusammengearbeitet und konsistente Lieferung und Qualität sichergestellt",
      "experience.joblogic.point6": "Zur Flutter-App mit Dart, Clean Architecture und GetX beigetragen",
      "experience.joblogic.tool1": "JIRA, Azure DevOps, Git",
      "experience.joblogic.tool2": "SwiftUI (persönliche Projekte)",
      "experience.joblogic.tool3": "Expertise in plattformübergreifenden Apps und Clean Architecture",
      "experience.yumyapps.desc": "Als Mid-Level iOS-Entwickler eingestiegen, der die End-to-End-App-Entwicklung leitete—vom ersten Build bis zum App Store Deployment, Wartung und Feature-Verbesserungen",
      "experience.yumyapps.heading": "Hauptverantwortlichkeiten und Leistungen:",
      "experience.yumyapps.point1": "iOS-Anwendungen mit Swift und UIKit gebaut und gepflegt",
      "experience.yumyapps.point2": "App-Funktionalität verbessert und kritische Bugs behoben, um das Produkt zu stabilisieren",
      "experience.yumyapps.point3": "Grundlage für ein neues Produkt mit sauberer Architektur und wiederverwendbaren Komponenten gelegt",
      "experience.yumyapps.point4": "Core Data für effiziente lokale Speicherung und Persistenz genutzt",
      "experience.yumyapps.point5": "MVVM- und MVC-Architekturmuster für skalierbaren Code angewendet",
      "experience.yumyapps.point6": "Mit funktionsübergreifenden Teams für pünktliche Lieferung und Qualitätssicherung zusammengearbeitet",
      "experience.vespert.desc": "iOS-Entwicklerkarriere bei Vespert Tech begonnen, verantwortlich für den Aufbau und die Pflege von iOS-Anwendungen",
      "experience.vespert.heading": "Hauptverantwortlichkeiten und Leistungen:",
      "experience.vespert.point1": "Über 15 Apps geliefert und Startups beim Wachstum und erfolgreichen Launch geholfen",
      "experience.vespert.point2": "iOS-Apps von Grund auf mit Swift, Objective-C und UIKit gebaut und gestartet",
      "experience.vespert.point3": "Schlüsselfunktionen wie In-App-Käufe, WebSockets und Google AdMob integriert",
      "experience.vespert.point4": "Legacy-Codebasen durch Einführung von MVVM/MVC-Architektur und Refactoring verbessert",
      "experience.vespert.point5": "In Agile-Teams mit aktiver Teilnahme an Stand-ups und Sprint-Planung zusammengearbeitet",
      "experience.vespert.point6": "Als leistungsstarkes Teammitglied für die Wertschöpfung für das Unternehmen anerkannt",

      // Projects Section
      "projects.title": "Meine Projekte 🛠️",
      "projects.subtitle": "Startups und Unternehmen, zu denen ich beigetragen habe, indem ich ihre Technologie von Grund auf aufgebaut und gestaltet habe",
      "projects.viewProject": "Projekt ansehen",
      "projects.openSourceTitle": "Open-Source-Projekte",
      "projects.moreProjects": "Mehr Projekte",
      "projects.joblogic.desc": "Field-Service-Management-Software-Suite für SaaS",
      "projects.jalsah.desc": "Social-Interaction-App für Echtzeit-Chat, interaktive Challenges und Spiele",
      "projects.familytime.desc": "Kindersicherungs-App mit Überwachungs- und Blockierungsfunktionen",
      "projects.alfalah.desc": "Islamische Lifestyle-App mit Koran, Gebeten, Duas, Qibla-Richtung und islamischem Kalender",
      "projects.getexpert.desc": "Service-Marktplatz-App, die Anbieter und Kunden verbindet",
      "projects.quokka.desc": "Social-Matchmaking-App für einminütige Sprachanrufe",
      "projects.scanner.desc": "Dienstprogramm für Dokumentenscanning, -bearbeitung und PDF-Generierung",
      "projects.skuscanner.desc": "Eine Bestandsverwaltungsanwendung, die Benutzern ermöglicht, Bestände zu verwalten, Barcodes zu generieren und zu scannen, Aufzeichnungen zu führen und detaillierte Berichte zur Nachverfolgung und Analyse zu erstellen",
      "projects.community.desc": "Social-App, die Benutzern ermöglicht, Hilfsgesuche in lokalen Gemeinschaften zu veröffentlichen",
      "projects.pisiffik.desc": "Moderne E-Commerce-App mit Online-Lebensmitteleinkauf, In-Store-Abholung, Lieferung nach Hause und integriertem Treueprämiensystem",
      "projects.my360key.desc": "App zur Echtzeit-Entdeckung nahegelegener Geschäfte mit kartenbasierter Navigation und intelligentem Türöffnen mit MapKit und Smart-Lock-APIs",
      "projects.lifesign.desc": "Eine sichere, kommunikationsorientierte mobile App mit Echtzeit-Messaging, um Benutzern zu helfen, durch private, sofortige Gespräche mit Freunden und Familie in Verbindung zu bleiben",
      "projects.flovso.desc": "Eine Social-Plattform für die Modeindustrie, die Benutzern ermöglicht, Meinungen zu teilen, an Diskussionen teilzunehmen und Community-Feedback zu Trends, Marken und Designern zu erhalten—mit nutzergenerierten Inhalten, Likes, Kommentaren",

      // Achievements Section
      "achievements.title": "Leistungen Und Zertifizierungen 🏆",
      "achievements.subtitle": "Leistungen, Zertifizierungen, Auszeichnungsschreiben und coole Dinge, die ich getan habe",
      "achievements.apple.title": "Apple-zertifizierte iOS-App-Entwicklung mit Swift",
      "achievements.apple.subtitle": "Apples offiziellen Lehrplan für iOS-Entwicklung mit Swift abgeschlossen. Praktische Erfahrung in App-Architektur, Swift-Programmierung, UI-Design und Deployment gesammelt. Durch die Swift Student Challenge und damit verbundene Apple-Lernprogramme teilgenommen.",
      "achievements.hackathon.title": "Hackathon-Gewinner bei AngelHack",
      "achievements.hackathon.subtitle": "Ersten Platz bei AngelHack für die Entwicklung einer mobilen Anwendung gewonnen, die Echtzeit-Notfallhilfe und Geo-Tracking bereitstellte. iOS-Entwicklung geleitet, skalierbare Architektur mit Swift und MVVM implementiert und die Lösung einem Expertengremium präsentiert.",
      "achievements.speaker.title": "Sprecher bei Mobile-Dev-Meetups und Konferenzen",
      "achievements.speaker.subtitle": "Technische Vorträge über iOS-Architekturmuster gehalten, darunter MVVM, Clean Swift und modulares App-Design. Einblicke in skalierbare Code-Praktiken, reale Debugging-Strategien und plattformübergreifende Entwicklung mit Flutter und SwiftUI bei verschiedenen lokalen Meetups und Online-Sessions geteilt.",

      // Blogs Section
      "blogs.title": "Blogs",
      "blogs.subtitle": "Mit Leidenschaft für die Entwicklung cooler Dinge schreibe ich gerne und unterrichte andere in dem, was ich gelernt habe.",

      // Talks Section
      "talks.title": "VORTRÄGE",
      "talks.subtitle": "ICH TEILE GERNE MEIN BEGRENZTES WISSEN UND BEKOMME EIN SPRECHER-ABZEICHEN 😅",

      // Podcast Section
      "podcast.title": "Podcast 🎙️",
      "podcast.subtitle": "ICH SPRECHE GERNE ÜBER MICH UND TECHNOLOGIE",

      // Contact Section
      "contact.title": "Kontaktiere mich ☎️",
      "contact.subtitle": "Ein Projekt besprechen oder einfach Hallo sagen? Mein Posteingang ist für alle offen",
      "contact.reachOut": "Kontaktiere mich!",
      "contact.openForOpportunities": "Offen für Möglichkeiten:",
      "contact.hireableYes": "Ja",
      "contact.hireableNo": "Nein",

      // Talk Card
      "talk.slides": "Folien",
      "talk.event": "Veranstaltung",

      // Resume Section
      "resume.title": "Lebenslauf",
      "resume.subtitle": "Lade gerne meinen Lebenslauf herunter",

      // Skills & Expertise Section
      "skillsExpertise.title": "Fähigkeiten & Expertise 🎯",
      "skillsExpertise.subtitle": "Technische Kernstärken und berufliche Fähigkeiten, die wirkungsvolle Ergebnisse erzielen",
      "skillsExpertise.iosDevelopment": "iOS-Entwicklung",
      "skillsExpertise.leadership": "Führung und Methodik",
      "skillsExpertise.philosophy": "Vibe Coding Philosophie",
      "skillsExpertise.crossPlatform": "Cross-Platform und Erweiterung",
      "skillsExpertise.appPublishing": "App-Veröffentlichung und Verteilung",

      // Stats Section
      "stats.title": "Schnelle Fakten 📊",
      "stats.subtitle": "Ein schneller Überblick über meine Karriere-Highlights, der Erfahrung, Leistungen und Wirkung zeigt",
      "stats.yearsExperience": "Jahre Erfahrung",
      "stats.projectsDelivered": "Gelieferte Projekte",
      "stats.uxImprovement": "UX-Verbesserung",
      "stats.clientsCollaborated": "Kunden, mit denen ich zusammengearbeitet habe",
      "stats.aiIntegrations": "KI-Integrationen",
      "stats.systemUptime": "System-Uptime",

      // Top Button
      "topButton.goToTop": "Nach oben",

      // How I Work Section
      "howIWork.title": "Wie ich arbeite 🔄",
      "howIWork.subtitle": "Ein klarer, wiederholbarer Prozess, der Ihr Projekt entrisikiert und pünktliche, budgetgerechte Lieferung gewährleistet.",
      "howIWork.phaseLabel": "PHASE {{number}}",
      "howIWork.phase1.title": "Entdeckung",
      "howIWork.phase1.desc": "Das Geschäft, die Nutzer und das Problem verstehen. Umfang, Prioritäten und Erfolgskriterien definieren, bevor eine einzige Codezeile geschrieben wird.",
      "howIWork.phase2.title": "Design",
      "howIWork.phase2.desc": "Zusammenarbeit an UX-Flows, Nutzerreisen und Systemarchitektur. Strategische Entscheidungen über Technologie, Struktur und Skalierbarkeit treffen.",
      "howIWork.phase3.title": "Entwicklung",
      "howIWork.phase3.desc": "Mit sauberer Architektur, modularem Code und automatisierten Tests entwickeln. Regelmäßige Demos und transparente Fortschrittsupdates.",
      "howIWork.phase4.title": "Release",
      "howIWork.phase4.desc": "QA, Performance-Optimierung, Store-Einreichungen und Produktions-Deployment. Ihr Produkt geht live mit Vertrauen und ohne offene Enden.",
      "howIWork.phase5.title": "Skalierung",
      "howIWork.phase5.desc": "Überwachen, analysieren, iterieren. Post-Launch-Support, Feature-Evolution und Performance-Verbesserungen, um das Wachstum Ihres Produkts aufrechtzuerhalten.",

      // What I Deliver Section
      "whatIDeliver.title": "Was ich liefere 📦",
      "whatIDeliver.subtitle": "End-to-End-Fähigkeiten, die Ihr Produkt vom Konzept zum Markt bringen — und es wachsen lassen.",
      "whatIDeliver.item1.title": "Geschäftsentdeckung und Anforderungen",
      "whatIDeliver.item1.desc": "Ich beginne damit, Ihre Geschäftsziele, Nutzerbedürfnisse und Marktkontext zu verstehen — damit wir das richtige Produkt bauen, nicht nur ein Produkt.",
      "whatIDeliver.item2.title": "Lösungs- und Systemdesign",
      "whatIDeliver.item2.desc": "Architekturentscheidungen, die skalieren. Ich entwerfe Systeme für Zuverlässigkeit, Performance und Wartbarkeit von Tag eins an.",
      "whatIDeliver.item3.title": "iOS-App-Entwicklung",
      "whatIDeliver.item3.desc": "Natives iOS-Entwicklung mit Swift, SwiftUI und UIKit. Saubere Architektur, modulare Codebasen, App-Store-fertige Performance.",
      "whatIDeliver.item4.title": "Backend- und API-Integrationen",
      "whatIDeliver.item4.desc": "Ihr Produkt mit Zahlungsgateways, Drittanbieter-Services, Cloud-Infrastruktur und benutzerdefinierten APIs verbinden — zuverlässig.",
      "whatIDeliver.item5.title": "KI-Feature-Integration",
      "whatIDeliver.item5.desc": "Intelligenz zu Produkten hinzufügen: On-Device-ML über Core ML, KI-gestützte Features und intelligente Automatisierung — wo es echten Nutzerwert schafft.",
      "whatIDeliver.item6.title": "CI/CD und automatisierte Lieferung",
      "whatIDeliver.item6.desc": "Automatisierte Pipelines, die Ihr Produkt bauen, testen und deployen. Keine manuellen Schritte. Keine Überraschungen. Jedes Release ist produktionsbereit.",
      "whatIDeliver.item7.title": "Qualitätssicherung und Release",
      "whatIDeliver.item7.desc": "Gründliche Tests, App-Store-Einreichungen und Release-Management. Ihr Produkt startet mit Vertrauen.",
      "whatIDeliver.item8.title": "Post-Launch-Überwachung und Wachstum",
      "whatIDeliver.item8.desc": "Absturzberichte, Performance-Überwachung, Nutzeranalysen und kontinuierliche Verbesserung. Ihr Produkt wird immer besser.",

      // Footer
      "footer.madeWith": "Gemacht mit ❤️"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    
    interpolation: {
      escapeValue: false,
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    }
  });

export default i18n;
